import { useTheme, styled } from "@monolith-forensics/monolith-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { EditRelayUserForm } from "../Forms";
import { BreadcrumbPageHeader } from "@/components/BreadCrumbHeader";
import { ArrowLeftIcon } from "lucide-react";
import { RemoveUserSection, UpdateUserRoleSection } from ".";
import RelayAPI from "../../../../../../api/RelayAPI";
import { useNavigate, useParams } from "react-router-dom";
import { useRelayInfo } from "../../../hooks";

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;

  overflow-y: auto;
  padding-right: 20px;
`;

const SectionWrapper = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  padding: 30px 0px;
  max-width: 600px;
`;

export const ProfileContent: React.FC = () => {
  const { user_id } = useParams();
  const { relayInfo } = useRelayInfo();
  const navigate = useNavigate();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { isLoading, data } = useQuery({
    queryKey: ["relay:users", { tenant_uuid: relayInfo?.tenant_uuid, user_id }],
    queryFn: () =>
      RelayAPI.getUsers({
        tenant_uuid: relayInfo?.tenant_uuid,
        user_id,
      }),
    enabled: !!relayInfo,
  });

  const refresh = () => {
    // refetch();
    queryClient.refetchQueries({ queryKey: ["relay:users"] });
  };

  const handleUpdate = () => {
    refresh();
    enqueueSnackbar("User updated successfully", { variant: "success" });
  };

  const onRemoveUser = () => {
    refresh();
  };

  const onRoleUpdate = () => {
    refresh();
  };

  if (isLoading) return null;

  const user = data?.data?.[0] || {};
  const tenant = user.tenants?.[0] || {};

  user.is_admin = tenant.is_admin;
  user.is_active = tenant.active;

  return (
    <StyledContent>
      <div>
        <BreadcrumbPageHeader
          pageName={`User Profile`}
          sectionName={
            <>
              <ArrowLeftIcon size={16} style={{ verticalAlign: "center" }} />
              Relay Users
            </>
          }
          onSectionClick={() =>
            navigate(window.location.pathname.split("/").slice(0, -1).join("/"))
          }
        />
        <div style={{ color: theme.palette.text.secondary, marginTop: 5 }}>
          {user.full_name} ({user.email})
        </div>
      </div>
      <SectionWrapper>
        <EditRelayUserForm defaultFormData={user} onSubmit={handleUpdate} />
      </SectionWrapper>

      <SectionWrapper>
        <RemoveUserSection
          user={user}
          relayInfo={relayInfo}
          onSubmit={onRemoveUser}
        />
      </SectionWrapper>

      <SectionWrapper>
        <UpdateUserRoleSection
          user={user}
          relayInfo={relayInfo}
          onSubmit={onRoleUpdate}
        />
      </SectionWrapper>
    </StyledContent>
  );
};
